import ConfirmOrRejectModal from 'src/features/base/components/ConfirmOrRejectModal'
import * as S from './ModalCountBatchesToReturn.styles'
import {
  CountTypeEnum, ExpirationBatchToResolve, ItemToResolve, ReturnBatchToResolve,
} from 'src/features/swap-or-return/SwapOrReturnTypes'
import { FormHelperText, Typography } from '@mui/material'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useCallback, useEffect } from 'react'
import { formatDateReplaceHour } from 'src/utils/formatDate'
import { toast } from 'react-toastify'
import { handleOnKeyDown } from 'src/utils/Form'

interface ModalCountBatchesToReturnProps {
  product: ItemToResolve
  show: boolean
  batchToResolve: ReturnBatchToResolve
  batchesToResolve: ReturnBatchToResolve[]
  setBatchesToResolve: React.Dispatch<React.SetStateAction<ReturnBatchToResolve[]>>
  onClose: () => void
}

interface ModalFormProps {
  batches: ExpirationBatchToResolve[]
}

export const ModalCountBatchesToReturn = ({
  product,
  show,
  batchToResolve,
  batchesToResolve,
  setBatchesToResolve,
  onClose,
}: ModalCountBatchesToReturnProps) => {
  const {
    control, reset, setError, watch, clearErrors, formState: { errors },
  } = useForm<ModalFormProps>({ defaultValues: { batches: batchToResolve.expirationBatches } })

  const { fields } = useFieldArray({
    control,
    name: 'batches',
  })

  const watchBatches = watch('batches')

  const totalCountedBatches = watchBatches.reduce((acc, batch) => acc + (batch.countedQuantity || 0), 0)

  const columns = [
    {
      name: 'Endereço',
      width: '50%',
    },
    {
      name: `Qtd. disponível (${product.measureUnit})`,
      width: '25%',
    },
    {
      name: `Qtd. contada (${product.measureUnit})`,
      width: '25%',
    },
  ]

  const validateFieldValues = useCallback((batch: ExpirationBatchToResolve, countedQuantity: number | null, index: number) => {
    if (countedQuantity === null) return

    if (countedQuantity < 0) {
      setError(`batches.${index}.countedQuantity`, {
        message: 'Não pode ser negativo',
      })
      return
    }

    if (countedQuantity > batch.availableQuantity) {
      setError(`batches.${index}.countedQuantity`, {
        message: 'Qtd. maior que a disponível',
      })
      return
    }

    if (!product.isKg && !new RegExp('^[0-9]*$').test(countedQuantity.toString())) {
      setError(`batches.${index}.countedQuantity`, {
        message: 'Deve ser inteiro',
      })
      return
    }

    clearErrors(`batches.${index}.countedQuantity`)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearErrors, setError])

  const onSubmit = () => {
    clearErrors()

    let hasBatchErrors = false
    watchBatches.forEach((batch, index) => {
      if (batch.countedQuantity !== undefined && batch.countedQuantity !== null) {
        if (batch.countedQuantity < 0) {
          setError(`batches.${index}.countedQuantity`, { message: 'Não pode ser negativo' })
          hasBatchErrors = true
        } else if (batch.countedQuantity > batch.availableQuantity) {
          setError(`batches.${index}.countedQuantity`, { message: 'Qtd. maior que a disponível' })
          hasBatchErrors = true
        } else if (!product.isKg && !new RegExp('^[0-9]*$').test(batch.countedQuantity.toString())) {
          setError(`batches.${index}.countedQuantity`, { message: 'Deve ser inteiro' })
          hasBatchErrors = true
        }
      }
    })

    if (hasBatchErrors) {
      toast.error('Corrija os campos com erro!')
      return
    }

    const totalQuantity = watchBatches.reduce((acc, batch) => acc + (Number(batch.countedQuantity) || 0), 0)

    if (totalQuantity > batchToResolve.quantity) {
      toast.error('Quantidade total contada maior que a quantidade a retornar para o lote')
      return
    }

    const updatedBatchesToResolve = batchesToResolve.map((batch) => {
      if (batch.idRequestItemBatch === batchToResolve.idRequestItemBatch) {
        return {
          ...batch,
          countedQuantity: totalQuantity,
          expirationBatches: batch.expirationBatches.map((expirationBatch) => {
            const matchedWatchBatch = watchBatches.find((watchBatch) => watchBatch.idExpirationBatch === expirationBatch.idExpirationBatch)
            if (matchedWatchBatch) {
              return {
                ...expirationBatch,
                countedQuantity: matchedWatchBatch.countedQuantity,
              }
            }
            return expirationBatch
          }),
        }
      }
      return batch
    })

    setBatchesToResolve(updatedBatchesToResolve)

    onClose()
  }

  useEffect(() => {
    reset({
      batches: batchToResolve.expirationBatches,
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, batchToResolve])

  return (
    <ConfirmOrRejectModal
      show={show}
      title={`Contagem de lotes a ${CountTypeEnum.RETURN.toUpperCase()}`}
      onConfirm={onSubmit}
      onReject={onClose}
      onClose={onClose}
      dataTestId='count-batches-return-quantity-modal-test-id'
      confirmButtonText='Confirmar'
      rejectButtonText='Cancelar'
      size='lg'
      customizableDescription={(
        <S.Form>
          <S.ProductInfoBox>
            {product.productDescription}
          </S.ProductInfoBox>
          <S.BoxInfos>
            <S.InfoText>Validade: {formatDateReplaceHour(new Date(batchToResolve.dueDate))}</S.InfoText>
            <S.InfoText>Quantidade a retornar do lote: {batchToResolve.quantity} {product.measureUnit}</S.InfoText>
          </S.BoxInfos>
          <S.ContainerRows data-testid='table-count-return-test-id'>
            <S.Row
              style={{
                position: 'sticky', top: 0, background: '#EFEFEF', zIndex: 1,
              }}
              data-testid='table-header-test-id'
            >
              {columns.map((column, index) => (
                <S.Column key={index} minWidth={column.width}>
                  <Typography sx={{ fontWeight: 'bold' }}>{column.name}</Typography>
                </S.Column>
              ))}
            </S.Row>
            {fields.map((batch, index) => (
              <S.Row
                key={batch.idExpirationBatch}
                data-testid='table-row-test-id'
              >
                <S.Column
                  minWidth={columns[0].width}
                  data-testid='address-column-test-id'
                >
                  <Typography>{batch.address}</Typography>
                </S.Column>
                <S.Column
                  minWidth={columns[1].width}
                  data-testid='available-quantity-column-test-id'
                >
                  <Typography>{batch.availableQuantity}</Typography>
                </S.Column>
                <S.Column
                  minWidth={columns[2].width}
                  data-testid='input-count-column-test-id'
                >
                  <Controller
                    name={`batches.${index}.countedQuantity`}
                    control={control}
                    defaultValue={batch.countedQuantity}
                    render={({ field }) => (
                      <>
                        <S.CustomTableTextField
                          sx={{
                            '& .MuiInputBase-input': {
                              height: '1rem !important',
                              paddingLeft: '6px !important',
                              paddingRight: '6px !important',
                            },
                          }}
                          {...field}
                          id='input-quantity'
                          type='number'
                          autoComplete='off'
                          label=' '
                          size='small'
                          onChange={(e) => {
                            const { value } = e.target
                            let convertedCountedQuantity: number | null = null
                            if (value === '') {
                              convertedCountedQuantity = null
                            } else {
                              convertedCountedQuantity = Number(value)
                            }
                            field.onChange({ ...e, target: { ...e.target, value: convertedCountedQuantity } })
                            validateFieldValues(batch, convertedCountedQuantity, index)
                          }}
                          error={!!errors.batches && !!errors.batches[index]}
                          InputLabelProps={{ shrink: false }}
                          inputProps={{
                            min: 0,
                            max: batch.availableQuantity,
                            'data-testid': `input-batch-${index}-counted-quantity-test-id`,
                          }}
                          onKeyDown={(e) => handleOnKeyDown(e, `input-batch-${index + 1}-counted-quantity-test-id`)}
                        />
                        {errors?.batches && errors.batches[index] && errors.batches[index].countedQuantity ? (
                          <FormHelperText error>
                            {errors?.batches[index]?.countedQuantity?.message ?? ''}
                          </FormHelperText>
                        ) : ''}
                      </>
                    )}
                  />
                </S.Column>
              </S.Row>
            ))}
          </S.ContainerRows>
          <S.CountedQuantity>Quantidade total contada: {totalCountedBatches} {product.measureUnit}</S.CountedQuantity>
          {totalCountedBatches > batchToResolve.quantity && (
            <S.CountedQuantityError>Quantidade contada maior que a quantidade a retornar do lote</S.CountedQuantityError>
          )}
        </S.Form>
      )}
    />
  )
}
